import { useEffect, useRef, useState } from 'react'
import './VerdictScreen.css'
import { DefaultScreenProps } from '../defaultScreenProps'
import { Verdict } from '../../utils/buildVerdict'
import { COPY, DYNAMIC_COPY, TAG_NOUN } from '../../constants/copy'
import { isTvMode } from '../../utils/tvMode'

/**
 * The OK that skipped the last stats line must not also restart the run
 * before the card has even been read.
 */
const RESTART_GRACE_MS = 600

type VerdictScreenProps = DefaultScreenProps & {
  sayVerdict: () => void
  getVerdict: () => Verdict
  playAgain: () => void
}

export const VerdictScreen = ({
  sayVerdict,
  getVerdict,
  playAgain,
}: VerdictScreenProps) => {
  const audioPlayedRef = useRef(false)
  const mountedAtRef = useRef(Date.now())
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [copied, setCopied] = useState(false)
  const tv = isTvMode()

  useEffect(() => {
    if (!audioPlayedRef.current) {
      sayVerdict()
      audioPlayedRef.current = true
    }
    // The one button is focused on every platform: Enter/OK restarts.
    buttonRef.current?.focus()
  }, [sayVerdict])

  // Enter with the button somehow unfocused only restores focus — same
  // rule as the victim screen, so the D-pad can never restart blind.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      if (document.activeElement === buttonRef.current) return
      if (
        document.activeElement instanceof HTMLButtonElement &&
        !isTvMode()
      ) {
        return
      }
      event.preventDefault()
      buttonRef.current?.focus()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const onPlayAgain = () => {
    if (Date.now() - mountedAtRef.current < RESTART_GRACE_MS) return
    playAgain()
  }

  const verdict = getVerdict()

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(verdict.shareText)
      setCopied(true)
    } catch (error) {
      console.warn('Clipboard unavailable:', error)
    }
  }

  return (
    <div className="verdictScreenContainer">
      <div className="verdictCard">
        <div className="verdictKicker">END OF THE LINE</div>
        <div className="verdictGradeRow">
          <span className="verdictGradeLabel">ETHICS GRADE</span>
          <span className={`verdictGrade grade-${verdict.grade}`}>
            {verdict.grade}
          </span>
        </div>
        <ul className="verdictStats">
          <li>
            Body count: <b>{verdict.bodyCount}</b>
          </li>
          <li>{DYNAMIC_COPY.SPARED_BY_MOST(verdict.sparedByMost)}</li>
          {verdict.favoritePrey && (
            <li>
              {DYNAMIC_COPY.FAVORITE_PREY(
                TAG_NOUN[verdict.favoritePrey.tag],
                verdict.favoritePrey.count,
              )}
            </li>
          )}
          {verdict.worstMoment && (
            <li>
              {DYNAMIC_COPY.WORST_MOMENT(
                verdict.worstMoment.name,
                verdict.worstMoment.percent,
              )}
            </li>
          )}
        </ul>
        <div className="verdictActions">
          <button
            ref={buttonRef}
            type="button"
            className="verdictButton verdictPrimary"
            onClick={onPlayAgain}
            onKeyDown={(event) => {
              // Explicit, like every other D-pad target in the game: some
              // remotes/WebViews deliver a raw Enter that never becomes a
              // native button activation.
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onPlayAgain()
              }
            }}
          >
            {COPY.PLAY_AGAIN}
          </button>
          {!tv && (
            <button
              type="button"
              className="verdictButton"
              onClick={onCopy}
              disabled={copied}
            >
              {copied ? COPY.COPIED : COPY.COPY_VERDICT}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
